import { useTenant } from '@/hooks/useTenant';
import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useCart } from '@/hooks/useCart';
import { supabase } from '@/lib/supabase';
import { resolveStoreName } from '@/lib/display';
import { getMainSiteUrl } from '@/lib/domain';
import { isMarketplaceTheme, isMarketplaceMixedAccount, isMarketplaceAccount } from '@/lib/marketplace';
import { fetchVendorRelation } from '@/lib/marketplaceCatalog';
import LanguageSwitcher from '@/components/feature/LanguageSwitcher';
import CurrencySwitcher from '@/components/feature/CurrencySwitcher';
import { getStoreCapabilities } from '@/lib/accountType';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import { trackReserveView, trackReserveClick } from '@/lib/reserveTracking';

// ── Google Fonts URL builder ──────────────────────────────────
function getGoogleFontUrl(fontFamily: string): string | null {
  const fontMap: Record<string, string> = {
    'Outfit': 'https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&display=swap',
    'DM Sans': 'https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap',
    'Inter': 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap',
    'Poppins': 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap',
    'Playfair Display': 'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;0,800;0,900;1,400;1,500&display=swap',
    'Lora': 'https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&display=swap',
    'Source Serif 4': 'https://fonts.googleapis.com/css2?family=Source+Serif+4:opsz,wght@8..60,300;8..60,400;8..60,500;8..60,600;8..60,700&display=swap',
    'Space Grotesk': 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap',
    'Cabinet Grotesk': 'https://fonts.googleapis.com/css2?family=Manrope:wght@300;400;500;600;700;800&display=swap',
    'JetBrains Mono': 'https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@300;400;500;600;700&display=swap',
    'Bricolage Grotesque': 'https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,300;12..96,400;12..96,500;12..96,600;12..96,700;12..96,800&display=swap',
    'Satoshi': 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap',
    'EB Garamond': 'https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400;1,500&display=swap',
    'Crimson Pro': 'https://fonts.googleapis.com/css2?family=Crimson+Pro:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400&display=swap',
    'Syne': 'https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&display=swap',
  };
  return fontMap[fontFamily] || null;
}

// ── Simple hex to OKLCH approximation ─────────────────────────
function hexToOklchChannels(hex: string): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;

  const toLinear = (c: number) => c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  const lr = toLinear(r);
  const lg = toLinear(g);
  const lb = toLinear(b);

  const x = 0.4124564 * lr + 0.3575761 * lg + 0.1804375 * lb;
  const y = 0.2126729 * lr + 0.7151522 * lg + 0.0721750 * lb;
  const z = 0.0193339 * lr + 0.1191920 * lg + 0.9503041 * lb;

  const x_ = 0.8189330101 * x + 0.3618667424 * y - 0.1288597137 * z;
  const y_ = 0.0329845436 * x + 0.9293118715 * y + 0.0361456387 * z;
  const z_ = 0.0482003018 * x + 0.2643662691 * y + 0.6338517070 * z;

  const xr = Math.cbrt(x_);
  const yr = Math.cbrt(y_);
  const zr = Math.cbrt(z_);

  const L = 0.2104542553 * xr + 0.7936177850 * yr - 0.0040720468 * zr;
  const a = 1.9779984951 * xr - 2.4285922050 * yr + 0.4505937099 * zr;
  const b2 = 0.0259040371 * xr + 0.7827717662 * yr - 0.8086757660 * zr;

  const C = Math.sqrt(a * a + b2 * b2);
  let H = Math.atan2(b2, a) * 180 / Math.PI;
  if (H < 0) H += 360;

  return `${L.toFixed(5)} ${C.toFixed(6)} ${H.toFixed(6)}`;
}

export default function TenantNavbar() {
  const { tenant, theme } = useTenant();
  const location = useLocation();
  const { user, logout } = useAuth(); // Get logged-in user (if any)
  const { count: cartCount } = useCart();
  const [navCounts, setNavCounts] = useState({ products: 0, services: 0, hebergements: 0, formations: 0, events: 0 });
  const [hebergementInstalled, setHebergementInstalled] = useState(false);
  const [logoFailedUrl, setLogoFailedUrl] = useState('');
  const [bookingInfo, setBookingInfo] = useState<{ hasBooking: boolean; defaultServiceId: number | null }>({ hasBooking: false, defaultServiceId: null });
  // Vrai si le compte connecté est vendeur de CETTE marketplace (rattachement userwebsite).
  const [isVendorHere, setIsVendorHere] = useState(false);

  // ── Dynamic Google Font loading ──────────────────────────────
  useEffect(() => {
    const fontFamily = theme?.fontFamily;
    if (!fontFamily) return;

    const fontUrl = getGoogleFontUrl(fontFamily);
    if (!fontUrl) return;

    const linkId = 'tenant-google-font';
    let linkEl = document.getElementById(linkId) as HTMLLinkElement | null;
    if (!linkEl) {
      linkEl = document.createElement('link');
      linkEl.id = linkId;
      linkEl.rel = 'stylesheet';
      document.head.appendChild(linkEl);
    }
    linkEl.href = fontUrl;

    const styleId = 'tenant-font-overrides';
    let styleEl = document.getElementById(styleId) as HTMLStyleElement | null;
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = `
      :root {
        --font-heading: '${fontFamily}', sans-serif;
        --font-body: '${fontFamily}', sans-serif;
        --font-label: '${fontFamily}', sans-serif;
      }
    `;

    return () => {
      if (linkEl) linkEl.remove();
      if (styleEl) styleEl.remove();
    };
  }, [theme?.fontFamily]);

  // ── Determine what the store actually offers (to build the nav) ──
  useEffect(() => {
    if (!tenant) return;
    let cancelled = false;
    (async () => {
      try {
        const [prodRes, svcRes, hebRes, formRes, evRes] = await Promise.all([
          supabase.from('product_items').select('id', { count: 'exact', head: true }).eq('idcommerce', tenant.id).eq('status', 'active'),
          supabase.from('nospartenairesservices').select('id', { count: 'exact', head: true }).eq('idcommerce', tenant.id),
          supabase.from('hebergement_properties').select('id', { count: 'exact', head: true }).eq('merchant_id', tenant.id).eq('published', true).eq('status', 'active'),
          supabase.from('formations').select('id', { count: 'exact', head: true }).eq('id_formateur', tenant.id).or('status.is.null,status.eq.published,status.eq.active'),
          supabase.from('events').select('id', { count: 'exact', head: true }).eq('id_user', String(tenant.id)),
        ]);
        if (!cancelled) {
          setNavCounts({
            products: prodRes.count || 0,
            services: svcRes.count || 0,
            hebergements: hebRes.count || 0,
            formations: formRes.count || 0,
            events: evRes.count || 0,
          });
        }
      } catch { /* silent */ }

      // L'app « Hébergement » doit être installée ET active pour exposer la
      // section : on ne l'affiche pas sur un simple compte Produit/Service.
      try {
        const { data: hebergApp } = await supabase
          .from('appvendeur')
          .select('status')
          .eq('idcommerce', tenant.id)
          .eq('nompage', 'hebergement')
          .maybeSingle();
        if (!cancelled) setHebergementInstalled(Number(hebergApp?.status) === 1);
      } catch { if (!cancelled) setHebergementInstalled(false); }

      // Réservation : calendrier ZCalendar par défaut du site (s'il existe)
      try {
        const [bkRes, cfgRes] = await Promise.all([
          supabase.from('booking_services').select('id').eq('owner', tenant.id).eq('statut', 1).order('id'),
          supabase.from('merchant_home_config').select('default_booking_service_id').eq('idcommerce', tenant.id).maybeSingle(),
        ]);
        if (cancelled) return;
        const ids = ((bkRes.data as { id: number }[]) || []).map((r) => r.id);
        const configured = (cfgRes.data as { default_booking_service_id?: number | null } | null)?.default_booking_service_id ?? null;
        const resolved = configured && ids.includes(configured) ? configured : (ids[0] ?? null);
        setBookingInfo({ hasBooking: ids.length > 0, defaultServiceId: resolved });
        // Suivi : le visiteur a vu le bouton Réserver (1 fois par session).
        if (ids.length > 0) void trackReserveView(tenant.id);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [tenant]);

  // Reconnaissance « vendeur de cette marketplace » : un compte n'est vendeur
  // ICI que s'il possède un rattachement (userwebsite) avec ce commerce. Le
  // typecompte vendeur est global, mais l'espace vendeur n'apparaît que sur ce site.
  useEffect(() => {
    const anyUser = user as { id?: number } | null;
    if (!anyUser?.id || !tenant?.id) {
      setIsVendorHere(false);
      return;
    }
    let active = true;
    fetchVendorRelation(tenant.id, anyUser.id)
      .then((rel) => { if (active) setIsVendorHere(Boolean(rel)); })
      .catch(() => { if (active) setIsVendorHere(false); });
    return () => { active = false; };
  }, [user, tenant?.id]);

  if (!tenant) return null;

  const storeName = resolveStoreName(tenant, theme);
  // Le logo provient d'abord des réglages de la boutique (Tableau de bord →
  // Ma boutique), puis seulement du thème : ainsi le changement de logo est
  // immédiatement visible sur le site public.
  const storeLogo = resolveMediaUrl(tenant.image || theme?.navImage);
  // Si l'image du logo est introuvable, on retombe proprement sur l'initiale
  // du nom plutôt que d'afficher un cadre vide.
  const logoBroken = Boolean(storeLogo) && logoFailedUrl === storeLogo;

  // ── Bouton compte : destination selon la session et le type de site ──
  //  • client connecté                → espace client (/mon-compte, site principal)
  //  • vendeur connecté sur marketplace → espace vendeur (/vendor-dashboard)
  const isMarketplaceStore = isMarketplaceAccount(tenant.type, tenant.typecompte) || isMarketplaceTheme(theme);
  const isSellerSession = isVendorHere;
  const accountExternal = isSellerSession && isMarketplaceStore ? '' : `${getMainSiteUrl()}/mon-compte`;
  const accountInternal = '/vendor-dashboard';

  // Le type de compte du marchand détermine les sections réellement proposées.
  const caps = getStoreCapabilities(tenant.type, tenant.typecompte);

  // ── Navigation : logo + Accueil + Nos produits / Nos services ──
  // Les liens s'adaptent automatiquement à ce que le commerce propose.
  const allLinks: Array<{ key: string; path: string; label: string; icon: string; isExternal: boolean }> = [
    { key: 'accueil', path: '/', label: 'Accueil', icon: 'ri-home-line', isExternal: false },
  ];

  if (caps.restaurant) {
    // Compte Restauration : Accueil → Menu (la carte) → panier → compte.
    allLinks.push({ key: 'menu', path: '/products', label: 'Menu', icon: 'ri-restaurant-2-line', isExternal: false });
  } else if (isMarketplaceMixedAccount(tenant.type, tenant.typecompte)) {
    // Compte Marketplace mixte : Accueil → Services → Produits → Professionnels.
    // Le panier et le compte sont affichés à droite de la barre.
    allLinks.push({ key: 'services', path: '/marketplace/services', label: 'Services', icon: 'ri-service-line', isExternal: false });
    allLinks.push({ key: 'produits', path: '/marketplace/produits', label: 'Produits', icon: 'ri-shopping-bag-line', isExternal: false });
    allLinks.push({ key: 'professionnels', path: '/vendeurs', label: 'Professionnels', icon: 'ri-store-2-line', isExternal: false });
    // Publier une annonce : accès direct au formulaire public de dépôt d'annonce.
    allLinks.push({ key: 'publier', path: '/publier-annonce', label: 'Publier une annonce', icon: 'ri-megaphone-line', isExternal: false });
  } else {
    // Ordre demandé : Services AVANT Produits (comptes Mixtes), puis Réserver.
    if (caps.services && navCounts.services > 0) {
      allLinks.push({ key: 'services', path: '/services', label: 'Nos services', icon: 'ri-service-line', isExternal: false });
    }
    if (caps.products && navCounts.products > 0) {
      allLinks.push({ key: 'produits', path: '/products', label: 'Nos produits', icon: 'ri-shopping-bag-line', isExternal: false });
    }
    // Réservation : ouvre directement le calendrier de disponibilité par défaut (ZCalendar).
    if (caps.services && bookingInfo.hasBooking) {
      allLinks.push({
        key: 'reserver',
        path: bookingInfo.defaultServiceId ? `/booking/${bookingInfo.defaultServiceId}` : '/booking',
        label: 'Réserver',
        icon: 'ri-calendar-check-line',
        isExternal: false,
      });
    }
  }
  // Section hébergement : visible uniquement si l'app Hébergement est installée
  // ET qu'au moins un bien est publié (jamais pour un compte Produit/Service seul).
  if (hebergementInstalled && navCounts.hebergements > 0) {
    allLinks.push({ key: 'hebergements', path: '/hebergements', label: 'Hébergements', icon: 'ri-hotel-bed-line', isExternal: false });
  }
  // Formations : le lien n'apparaît que si le commerce en a publié au moins une.
  if (navCounts.formations > 0) {
    allLinks.push({ key: 'formations', path: '/formations', label: 'Formations', icon: 'ri-graduation-cap-line', isExternal: false });
  }
  // Événements : accès à l'agenda dès qu'un événement est enregistré.
  if (navCounts.events > 0) {
    allLinks.push({ key: 'evenements', path: '/events', label: 'Événements', icon: 'ri-calendar-event-line', isExternal: false });
  }
  // Sur une marketplace : catégories + vendeurs, et l'espace vendeur si connecté en tant que vendeur
  if (isMarketplaceTheme(theme) && !isMarketplaceMixedAccount(tenant.type, tenant.typecompte)) {
    allLinks.push({ key: 'categories', path: '/categories', label: 'Catégories', icon: 'ri-apps-2-line', isExternal: false });
    allLinks.push({ key: 'annonces', path: '/annonces', label: 'Annonces', icon: 'ri-megaphone-line', isExternal: false });
    // Publier une annonce : le visiteur (même sans compte) arrive sur le
    // formulaire public de dépôt d'annonce de la marketplace.
    allLinks.push({ key: 'publier', path: '/publier-annonce', label: 'Publier une annonce', icon: 'ri-add-circle-line', isExternal: false });
    allLinks.push({ key: 'vendeurs', path: '/vendeurs', label: 'Nos vendeurs', icon: 'ri-store-2-line', isExternal: false });
    if (user && isVendorHere) {
      allLinks.push({ key: 'espace', path: '/vendor-dashboard', label: 'Mon tableau de bord', icon: 'ri-dashboard-line', isExternal: false });
    } else {
      allLinks.push({ key: 'vendeur', path: '/vendeur/inscription', label: 'Vendre', icon: 'ri-star-line', isExternal: false });
    }
  }

  // Les pages personnalisées du marchand ne sont plus affichées automatiquement
  // dans la navbar principale. Le marchand pourra choisir de les ajouter depuis
  // son tableau de bord. On garde l'ordre : Accueil → Nos produits → Nos services.

  const isActive = (path: string, isExternal: boolean) => {
    if (isExternal) return false;
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* ── Color overrides (injected when merchant customized colors) ── */}
      {(theme?.primaryColor || theme?.accentColor) && (
        <style>{`
          :root {
            ${theme.primaryColor ? `--primary-500: ${hexToOklchChannels(theme.primaryColor)};` : ''}
            ${theme.accentColor ? `--accent-500: ${hexToOklchChannels(theme.accentColor)};` : ''}
          }
        `}</style>
      )}

      <nav className="sticky top-0 z-50 bg-background-50 border-b border-background-200/70">
        <div className="w-full px-3 md:px-6">
          <div className="flex items-center gap-2 md:gap-3 h-14">
            {/* Logo / Store name */}
            <Link to="/" className="flex items-center gap-2.5 cursor-pointer no-underline shrink-0">
              {storeLogo && !logoBroken ? (
                <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 border border-background-200/70 bg-background-100">
                  <img
                    src={storeLogo}
                    alt={storeName}
                    className="w-full h-full object-contain"
                    onError={() => setLogoFailedUrl(storeLogo)}
                  />
                </div>
              ) : (
                <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-background-50 text-sm font-bold font-heading">
                    {storeName.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <span className="text-sm font-bold font-heading text-foreground-950 hidden sm:block">
                {storeName}
              </span>
            </Link>

            {/* Liens de navigation : TOUJOURS visibles, quelle que soit la
                largeur de l'écran. Sur petit écran la rangée défile
                horizontalement (Services, Produits, Réserver… restent
                accessibles) : plus besoin d'icône hamburger. */}
            {/* L'enveloppe pousse la rangée de liens vers la droite ; si les
                liens sont trop nombreux, la rangée se réduit et défile
                horizontalement (le premier lien reste accessible). */}
            <div className="flex-1 min-w-0 flex justify-end overflow-hidden">
            <nav
              className="min-w-0 flex items-center gap-0.5 overflow-x-auto no-scrollbar py-1 md:pr-2"
              aria-label="Navigation de la boutique"
            >
              {allLinks.map((link) => (
                link.isExternal ? (
                  <a
                    key={link.key}
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors whitespace-nowrap no-underline text-foreground-600 hover:text-foreground-900 hover:bg-background-100"
                  >
                    <i className={`${link.icon} text-sm`}></i>
                    <span>{link.label}</span>
                    <i className="ri-external-link-line text-[10px] ml-0.5"></i>
                  </a>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={link.key === 'reserver' ? () => { void trackReserveClick(tenant.id); } : undefined}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors whitespace-nowrap no-underline ${
                      isActive(link.path, false)
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                    }`}
                  >
                    <i className={`${link.icon} text-sm`}></i>
                    <span>{link.label}</span>
                  </Link>
                )
              ))}
            </nav>
            </div>

            <div className="flex items-center gap-1 shrink-0">
              {/* ── Cart button (uniquement si le site vend des produits) ── */}
              {caps.products && (
                <Link
                  to="/cart"
                  className="relative flex items-center justify-center w-9 h-9 rounded-full text-foreground-600 hover:text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer no-underline ml-1"
                  aria-label="Panier"
                >
                  <i className="ri-shopping-cart-line text-lg"></i>
                  {cartCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary-500 text-background-50 text-[10px] font-bold flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                </Link>
              )}

              {/* ── Language switcher ── */}
              <LanguageSwitcher compact className="mr-1 hidden sm:flex" />

              {/* ── Currency switcher (équivalence dans la monnaie du visiteur) ── */}
              <CurrencySwitcher compact nativeCurrency={tenant?.monaie || 'MAD'} className="mr-1 hidden sm:flex" />

              {/* ── Bouton compte : redirige selon le rôle de la session ── */}
              <div className="flex items-center gap-1 md:ml-2">
                {user ? (
                  <>
                    {accountExternal ? (
                      <a
                        href={accountExternal}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors whitespace-nowrap no-underline bg-accent-100/70 text-accent-700 hover:bg-accent-200/70"
                      >
                        <span className="w-5 h-5 rounded-full bg-accent-300 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {user.image ? (
                            <img src={user.image} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <i className="ri-user-line text-[10px] text-accent-600"></i>
                          )}
                        </span>
                        <span className="hidden md:inline max-w-[100px] truncate">{user.name}</span>
                      </a>
                    ) : (
                      <Link
                        to={accountInternal}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors whitespace-nowrap no-underline bg-accent-100/70 text-accent-700 hover:bg-accent-200/70"
                      >
                        <span className="w-5 h-5 rounded-full bg-accent-300 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {user.image ? (
                            <img src={user.image} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <i className="ri-store-2-line text-[10px] text-accent-600"></i>
                          )}
                        </span>
                        <span className="hidden md:inline max-w-[100px] truncate">{user.nomcommerce || user.name}</span>
                      </Link>
                    )}
                    <button
                      type="button"
                      onClick={logout}
                      aria-label="Déconnexion"
                      title="Déconnexion"
                      className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 hover:text-red-500 transition-colors cursor-pointer"
                    >
                      <i className="ri-logout-box-line text-sm"></i>
                    </button>
                  </>
                ) : (
                  <Link
                    to="/login-client"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors whitespace-nowrap no-underline bg-accent-500 text-background-50 hover:bg-accent-600"
                  >
                    <i className="ri-user-line text-sm"></i>
                    <span className="hidden md:inline">Connexion</span>
                  </Link>
                )}
              </div>
            </div>
          </div>

        </div>
      </nav>
    </>
  );
}